import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { WebPageSchema, ArticleSchema } from '@/components/feature/SchemaOrg';
import Breadcrumb, { BreadcrumbWrapper } from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  window.scrollTo({ top: 0, behavior: 'instant' });

  const video = videos.find((v) => v.slug === slug);
  const article = articles.find((a) => a.slug === slug);
  const content = video || article;

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 text-center py-20">
          <p className="text-foreground-500">内容不存在</p>
          <button onClick={() => navigate('/')} className="mt-4 text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</button>
        </main>
        <Footer />
      </div>
    );
  }

  const isVideo = 'episode' in content;
  const title = content.title;
  const description = isVideo ? (content as typeof videos[0]).description : (content as typeof articles[0]).excerpt;
  const categoryName = categories.find((c) => c.slug === content.category)?.name || '';
  const relatedItems = videos.filter((v) => v.category === content.category && v.slug !== slug).slice(0, 4);
  const tags = content.tags;

  const handleRelatedClick = (videoSlug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => navigate(`/article/${videoSlug}`), 150);
  };

  return (
    <>
      <SEOHead
        title={`${title} - 嫩草视频高清在线观看`}
        description={description.slice(0, 160)}
        keywords={`嫩草视频,${title},${tags.join(',')},高清在线观看,${categoryName}`}
        canonicalUrl={`https://www.nencao.com/article/${slug}`}
        ogType="article"
      />
      <WebPageSchema
        title={`${title} - 嫩草视频`}
        description={description}
        url={`https://www.nencao.com/article/${slug}`}
      />
      <ArticleSchema
        title={title}
        description={description}
        author="嫩草视频编辑部"
        date={content.createdAt || content.publishDate}
        url={`https://www.nencao.com/article/${slug}`}
        image={content.thumbnail}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <BreadcrumbWrapper>
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${content.category}` },
                { label: title },
              ]}
            />
          </BreadcrumbWrapper>

          <article className="py-8 md:py-12">
            <div className="max-w-[1000px] mx-auto px-4 md:px-6">
              {isVideo && (
                <div className="relative aspect-video rounded-lg overflow-hidden mb-8 bg-background-900">
                  <img
                    src={content.thumbnail}
                    alt={`${title} - 嫩草视频`}
                    title={`${title} - 嫩草视频热播影视剧`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-500 transition-all hover:scale-110">
                      <i className="ri-play-fill text-4xl"></i>
                    </div>
                  </div>
                </div>
              )}

              <header className="mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">{title}</h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                  {isVideo && (
                    <>
                      <span className="flex items-center gap-1">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500"></i>
                        </div>
                        {(content as typeof videos[0]).rating}
                      </span>
                      <span>{(content as typeof videos[0]).episode}</span>
                      <span>{(content as typeof videos[0]).year}</span>
                      <span>{(content as typeof videos[0]).region}</span>
                    </>
                  )}
                  {!isVideo && (
                    <>
                      <span>{(content as typeof articles[0]).author}</span>
                      <span>{(content as typeof articles[0]).publishDate}</span>
                      <span>{(content as typeof articles[0]).views.toLocaleString()}次阅读</span>
                    </>
                  )}
                </div>

                {isVideo && (
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-foreground-600">
                    <span>导演：{(content as typeof videos[0]).director}</span>
                    <span>主演：{(content as typeof videos[0]).actors.join('、')}</span>
                  </div>
                )}
              </header>

              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <section className="prose prose-sm max-w-none mb-10">
                <h2 className="text-lg font-semibold text-foreground-900 mb-3">
                  {isVideo ? '剧情简介' : '文章内容'}
                </h2>
                {isVideo ? (
                  <p className="text-foreground-700 leading-relaxed">{description}</p>
                ) : (
                  <div
                    className="text-foreground-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: (content as typeof articles[0]).content }}
                  />
                )}
              </section>

              <section>
                <h2 className="text-lg font-semibold text-foreground-900 mb-5">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedItems.map((item) => (
                    <VideoCard key={item.id} video={item} />
                  ))}
                </div>
              </section>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}